import React, { Suspense, useRef, useState, memo, useMemo, useCallback, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useGLTF, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

// 3D Logo Model Component - Enhanced with interactions
const LogoModel = memo(function LogoModel({ 
  autoRotate = true, 
  baseRotationSpeed = 0.01, // 60-90s per revolution
  hoverRotationSpeed = 0.05,
  externalHovered = false,
  loadingMode = false // New prop for continuous spinning
}) {
  const { scene } = useGLTF('/images/3d Logo.gltf');
  const modelRef = useRef();
  const groupRef = useRef();
  const [internalHovered, setInternalHovered] = useState(false);

  // Memoize the cloned scene to avoid recreating it on every render
  const clonedScene = useMemo(() => {
    if (scene) {
      return scene.clone();
    }
    return null;
  }, [scene]);

              // Enhance materials for chrome/metal appearance - memoized
              useMemo(() => {
                if (clonedScene) {
                  clonedScene.traverse((child) => {
                    if (child.isMesh && child.material) {
                      // Make material chrome/metal with high reflectivity
                      if (Array.isArray(child.material)) {
                        child.material.forEach(mat => {
                          if (mat) {
                            mat.needsUpdate = true;
                            mat.transparent = false;
                            mat.opacity = 1.0;
                            if (mat.color) {
                              mat.color.convertSRGBToLinear();
                            }
                            if (mat.type === 'MeshStandardMaterial') {
                          // Perfect chrome properties for dynamic shine and reflections
                          mat.roughness = 0.02; // Ultra low for mirror reflections
                          mat.metalness = 1.0; // Full metalness for chrome
                          mat.envMapIntensity = 2.0; // Very high for strong reflections
                          mat.clearcoat = 1.0; // Maximum clearcoat for wet chrome look
                          mat.clearcoatRoughness = 0.01; // Ultra sharp highlights
                          // Medium chrome color for contrast
                          mat.color.setHex(0x888888); // Medium gray chrome base
                          // Subtle emissive for depth
                          mat.emissive.setHex(0x222222); // Subtle emissive
                          mat.emissiveIntensity = 0.2; // Minimal glow
                              // Ensure material reflects environment properly
                              mat.needsUpdate = true;
                            }
                          }
                        });
                      } else {
                        child.material.needsUpdate = true;
                        child.material.transparent = false;
                        child.material.opacity = 1.0;
                        if (child.material.color) {
                          child.material.color.convertSRGBToLinear();
                        }
                        if (child.material.type === 'MeshStandardMaterial') {
                          // Perfect chrome properties for dynamic shine and reflections
                          child.material.roughness = 0.02; // Ultra low for mirror reflections
                          child.material.metalness = 1.0; // Full metalness for chrome
                          child.material.envMapIntensity = 2.0; // Very high for strong reflections
                          child.material.clearcoat = 1.0; // Maximum clearcoat for wet chrome look
                          child.material.clearcoatRoughness = 0.01; // Ultra sharp highlights
                          // Medium chrome color for contrast
                          child.material.color.setHex(0x888888); // Medium gray chrome base
                          // Subtle emissive for depth
                          child.material.emissive.setHex(0x222222); // Subtle emissive
                          child.material.emissiveIntensity = 0.2; // Minimal glow
                          // Ensure material reflects environment properly
                          child.material.needsUpdate = true;
                        }
                      }
                    }
                  });
                }
              }, [clonedScene]);

  // Hover interactions now controlled externally

  // Set initial rotation for optimal chrome viewing angle
  useEffect(() => {
    if (modelRef.current) {
      modelRef.current.rotation.y = 0.3; // Less angled, more straight-on view
      modelRef.current.rotation.x = 0.1; // Minimal tilt for chrome visibility
    }
  }, []);

  // Animation frame updates with gentle swaying and hover pop
  useFrame((state, delta) => {
    if (modelRef.current && groupRef.current) {
      // Respect reduced motion preference
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      if (!prefersReducedMotion) {
        if (loadingMode) {
          // Loading mode: continuous smooth spinning
          modelRef.current.rotation.y += baseRotationSpeed;
          modelRef.current.rotation.x = 0.1; // Slight tilt for visual interest
          modelRef.current.rotation.z = 0;
          groupRef.current.position.y = 0;
        } else {
          // Gentle swaying movement around the optimal chrome viewing angle
          const swayAmount = 0.08; // Slight sway range
          const swaySpeed = 0.6; // Gentle sway speed
          
          // Sway on Y-axis (side to side) around the base angle
          modelRef.current.rotation.y = 0.3 + Math.sin(state.clock.elapsedTime * swaySpeed) * swayAmount;
          
          // Very minimal sway on X-axis (up and down)
          modelRef.current.rotation.x = 0.1 + Math.cos(state.clock.elapsedTime * swaySpeed * 0.7) * 0.04;
          
          // Barely noticeable sway on Z-axis (twist)
          modelRef.current.rotation.z = Math.sin(state.clock.elapsedTime * swaySpeed * 0.5) * 0.03;
          
          // Subtle floating motion
          groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.8) * 0.03;
        }
      } else {
        // Static positioning for reduced motion
        modelRef.current.rotation.y = 0.3;
        modelRef.current.rotation.x = 0.1;
        modelRef.current.rotation.z = 0;
        groupRef.current.position.y = 0;
      }
      
      // Hover pop effect - use external hover state
      if (externalHovered) {
        // Scale up slightly with smooth transition
        modelRef.current.scale.lerp(new THREE.Vector3(1.08, 1.08, 1.08), 0.1);
      } else {
        // Return to normal scale
        modelRef.current.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
      }
    }
  });

  if (!clonedScene) {
    return null;
  }

  // Scale down the model significantly in loading mode
  const modelScale = loadingMode ? 0.15 : 1;

  return (
    <group ref={groupRef}>
      <group ref={modelRef}>
        <primitive
          object={clonedScene}
          scale={[modelScale, modelScale, modelScale]}
        />
      </group>
    </group>
  );
});

// Loading fallback component - shows a chrome placeholder while loading
function LogoLoadingFallback() {
  return (
    <group>
      {/* Create a stylized "O" shape as placeholder with chrome appearance */}
      <mesh>
        <torusGeometry args={[0.8, 0.2, 16, 32]} />
        <meshStandardMaterial 
          color="#4A4A4A" 
          metalness={1.0}
          roughness={0.1}
        />
      </mesh>
      <mesh>
        <torusGeometry args={[0.6, 0.15, 16, 32]} />
        <meshStandardMaterial 
          color="#5A5A5A" 
          metalness={1.0}
          roughness={0.1}
        />
      </mesh>
      <mesh>
        <torusGeometry args={[0.4, 0.1, 16, 32]} />
        <meshStandardMaterial 
          color="#6A6A6A" 
          metalness={1.0}
          roughness={0.1}
        />
      </mesh>
    </group>
  );
}

// Main 3D Logo Component - Enhanced with interactions
const Logo3D = memo(function Logo3D({ 
  className = '',
  autoRotate = true,
  baseRotationSpeed = 0.01,
  hoverRotationSpeed = 0.05,
  enableControls = false,
  showEnvironment = true,
  cameraPosition = [0, 0, 3.5],
  fov = 60,
  onHover = () => {},
  onLeave = () => {},
  isHovered = false,
  glowIntensity = 0,
  heroAreaMode = false,
  loadingMode = false // New prop for continuous spinning
}) {
  const [responsiveCamera, setResponsiveCamera] = useState(cameraPosition);
  const [responsiveFov, setResponsiveFov] = useState(fov);

  // Responsive camera positioning for optimal logo scaling
  useEffect(() => {
    const updateCamera = () => {
      const isMobile = window.innerWidth < 768;
      
      if (heroAreaMode) {
        // Hero area mode: adjust for available space next to sidebar
        const sidebarWidth = window.innerWidth < 768 ? 64 : 256; // Sidebar width (collapsed vs expanded)
        const availableWidth = window.innerWidth - sidebarWidth;
        const isNarrowArea = availableWidth < 800;
        
        if (isMobile || isNarrowArea) {
          // Narrow area: tiny logo with 30-40% empty space around it - 1-2% of hero width
          setResponsiveCamera([0, 0, 50.0]);
          setResponsiveFov(40);
        } else {
          // Wide hero area: tiny logo with 30-40% empty space around it - 0.5-1.5% of hero width
          setResponsiveCamera([0, 0, 60.0]);
          setResponsiveFov(25);
        }
      } else {
        // Full viewport mode (legacy)
        if (isMobile) {
          setResponsiveCamera([0, 0, 3.2]);
          setResponsiveFov(65);
        } else {
          setResponsiveCamera([0, 0, 4.0]);
          setResponsiveFov(55);
        }
      }
    };

    updateCamera();
    window.addEventListener('resize', updateCamera);
    return () => window.removeEventListener('resize', updateCamera);
  }, [heroAreaMode]);

  return (
    <div className={`relative ${className}`}>
      <Canvas
        camera={{ position: responsiveCamera, fov: responsiveFov }}
        dpr={[1, 2]}
        gl={{ 
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: loadingMode ? 2.0 : 1.3 // Balanced for chrome shine with contrast
        }}
        style={{ 
          background: 'transparent',
          width: '100%',
          height: '100%'
        }}
      >
        {/* Dynamic Chrome Lighting Setup - Creates rotating highlights and reflections */}
        
        {/* Moderate ambient light for base illumination */}
        <ambientLight intensity={loadingMode ? 1.0 : 0.6} color="#FFFFFF" />
        
        {/* Intense key light from front-upper-left - creates bright primary highlight */}
        <directionalLight 
          position={[-12, 15, 8]}
          intensity={loadingMode ? 9.0 : 7.0}
          color="#FFFFFF"
          castShadow
          shadow-mapSize-width={4096}
          shadow-mapSize-height={4096}
        />
        
        {/* Strong rim light from upper-right - creates chrome edge streaks */}
        <directionalLight 
          position={[15, 12, -5]} 
          intensity={loadingMode ? 8.0 : 6.0} 
          color="#FFFFFF"
        />
        
        {/* Accent light from top - creates dynamic highlights as logo rotates */}
        <directionalLight 
          position={[-8, 20, 0]} 
          intensity={loadingMode ? 7.5 : 5.5} 
          color="#FFFFFF"
        />
        
        {/* Fill light from below with slight warmth - adds depth to chrome */}
        <directionalLight 
          position={[0, -10, 10]} 
          intensity={loadingMode ? 3.5 : 2.0} 
          color="#DDDDDD"
        />
        
        {/* Intense point lights for chrome highlights - create bright specular spots */}
        <pointLight 
          position={[10, 10, 10]} 
          intensity={loadingMode ? 8.0 : 6.0} 
          color="#FFFFFF"
          distance={50}
        />
        <pointLight 
          position={[-10, 10, 10]} 
          intensity={loadingMode ? 8.0 : 6.0} 
          color="#FFFFFF"
          distance={50}
        />
        <pointLight 
          position={[0, 15, -10]} 
          intensity={loadingMode ? 7.0 : 5.0} 
          color="#FFFFFF"
          distance={50}
        />
        
        {/* Hemisphere light for natural contrast */}
        <hemisphereLight 
          skyColor="#FFFFFF"
          groundColor="#444444"
          intensity={loadingMode ? 2.0 : 1.2}
        />

        {/* Optional orbit controls for interaction */}
        {enableControls && (
          <OrbitControls
            enableZoom={false}
            enablePan={false}
            maxPolarAngle={Math.PI / 2}
            minPolarAngle={Math.PI / 2}
            autoRotate={false}
          />
        )}

        {/* 3D Logo Model */}
        <Suspense fallback={<LogoLoadingFallback />}>
          <LogoModel 
            autoRotate={autoRotate}
            baseRotationSpeed={baseRotationSpeed}
            hoverRotationSpeed={hoverRotationSpeed}
            onHover={onHover}
            onLeave={onLeave}
            isHovered={isHovered}
            externalHovered={isHovered}
            glowIntensity={glowIntensity}
            loadingMode={loadingMode}
          />
        </Suspense>
      </Canvas>
    </div>
  );
});

export default Logo3D;

// Preload the logo model for better performance
if (typeof window !== 'undefined') {
  useGLTF.preload('/images/3d Logo.gltf');
}

